import Head from 'next/head'
import Script from 'next/script'
import { FaCreditCard, FaLock, FaShieldAlt, FaCheckCircle } from 'react-icons/fa'
import { LegalPageLayout, LegalSection, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function PaymentsSecurity() {
  return (
    <>
      <Head>
        <title>How StringsSG Secures Your Payment Details | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="At StringsSG, your payment security is of utmost importance. We want you to have peace of mind when transacting through our platform."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/payments-security" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="How StringsSG Secures Your Payment Details | StringsSG"/>
        <meta property="og:description" content="At StringsSG, your payment security is of utmost importance. We want you to have peace of mind when transacting through our platform." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/payments-security"/>
        <meta name="twitter:title" content="How StringsSG Secures Your Payment Details | StringsSG"/> 
        <meta name="twitter:description" content="At StringsSG, your payment security is of utmost importance. We want you to have peace of mind when transacting through our platform."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/payments-security"/>
      </Head>
      
      <LegalPageLayout
        title="Payment Security"
        subtitle="At StringsSG, your payment security is of utmost importance. We want you to have peace of mind when transacting through our platform."
        icon={FaCreditCard}
        iconBgColor="from-green-500 to-emerald-600"
        heroBgColor="from-slate-900 via-slate-800 to-green-900"
      >
        <LegalHighlight color="green">
          <div className="flex items-start gap-3">
            <FaShieldAlt className="text-green-600 text-xl mt-1 shrink-0" />
            <p>
              At StringsSG, your payment security is of utmost importance. We want you to have peace of mind when transacting through our platform. Here&apos;s how we ensure your payment details are protected:
            </p>
          </div>
        </LegalHighlight>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FaLock className="text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900">We Don&apos;t Store Your Payment Details</h3>
            </div>
            <p className="text-slate-600 text-sm">StringsSG does not retain or store your credit card or confidential payment information. Your data privacy is a priority.</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FaCreditCard className="text-indigo-600" />
              </div>
              <h3 className="font-bold text-slate-900">Powered by Stripe</h3>
            </div>
            <p className="text-slate-600 text-sm">We&apos;ve partnered with Stripe Inc, a trusted payment processing company, to handle your payments. Stripe is known for its advanced security measures.</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FaCheckCircle className="text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900">Payment Options</h3>
            </div>
            <p className="text-slate-600 text-sm">You can choose from various payment methods, including Credit/Debit cards, PayNow, Google Pay, or Apple Pay, to suit your convenience.</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaShieldAlt className="text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900">Secure Transactions</h3>
            </div>
            <p className="text-slate-600 text-sm">When you make a payment through StringsSG, your transaction is safeguarded by the latest Transport Layer Security (TLS 1.3) and a reliable payment network.</p>
          </div>
        </div>

        <LegalSection title="Advanced TLS Encryption">
          <p>
            We use the latest Transport Layer Security (TLS 1.3) protocols to protect your data during transmission. This advanced encryption technology ensures that your payment information is shielded from prying eyes, providing an extra layer of security.
          </p>
        </LegalSection>

        <LegalSection title="Data Privacy">
          <p>
            We are committed to safeguarding your data. StringsSG does not retain or store any of your credit card or confidential payment information. Your sensitive data is never accessible to outsiders, ensuring your privacy and security.
          </p>
        </LegalSection>

        <div className="bg-linear-to-br from-slate-50 to-green-50 rounded-xl p-6 border border-slate-200 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Your Data is Secure</h2>
          <p className="text-slate-600 leading-relaxed">
            We want you to know that your trust in us is well-placed. We have taken every measure to protect your data and 
            maintain a secure environment for your transactions. Your information is never exposed to outsiders, and our state-of-the-art TLS encryption ensures that your data remains confidential and secure.
          </p>
        </div>

        <LegalSection title="Trust in Stripe">
          <p className="mb-4">
            Stripe is renowned for its commitment to protecting payment and credit card details. They employ state-of-the-art encryption and 
            security protocols to ensure your data is safe. With Stripe, you can be confident that your payment information is handled with the utmost care and security.
          </p>
          <p className="mb-4">
            Your trust in us is essential, and we are dedicated to providing a secure and seamless payment experience on the StringsSG platform.
          </p>
          <p>
            Learn more about{' '}
            <a className="text-green-600 hover:text-green-700 font-medium" href="https://stripe.com/docs/security" target="_blank" rel="nofollow noopener noreferrer">
              Stripe Security
            </a>.
          </p>
        </LegalSection>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
